import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import PhoneInput from "@/components/PhoneInput";
import { api, formatAxiosError } from "@/lib/api";
import { RECAPTCHA_SITE_KEY, loadRecaptchaScript, getRecaptchaToken } from "@/lib/recaptcha";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useT } from "@/hooks/useT";

const PROPERTY_TYPES_SQ = [
  "Hotel",
  "Boutique Hotel",
  "Hostel",
  "Apartamente",
  "Vila",
  "Bed & Breakfast",
  "Resort",
  "Guest House",
  "Tjetër",
];
const PROPERTY_TYPES_EN = [
  "Hotel",
  "Boutique Hotel",
  "Hostel",
  "Apartments",
  "Villa",
  "Bed & Breakfast",
  "Resort",
  "Guest House",
  "Other",
];

const JOB_ROLES_SQ = [
  "Pronar",
  "Menaxher i Përgjithshëm",
  "Menaxher Operacionesh",
  "Menaxher Recepsioni",
  "IT",
  "Konsulent",
  "Tjetër",
];
const JOB_ROLES_EN = [
  "Owner",
  "General Manager",
  "Operations Manager",
  "Front Desk Manager",
  "IT",
  "Consultant",
  "Other",
];

const ROOMS = ["1–10", "11–25", "26–50", "51–100", "100+"];

export default function Demo() {
  const t = useT();
  useEffect(() => { loadRecaptchaScript(); }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    job_role: "",
    property_type: "",
    rooms: "",
    message: "",
  });
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const captcha_token = await getRecaptchaToken("demo_submit");
      if (RECAPTCHA_SITE_KEY && !captcha_token) {
        toast.error(t("Verifikimi i sigurisë dështoi. Ju lutem provoni sërish.", "Security check failed. Please try again."));
        setLoading(false);
        return;
      }
      const dial = country?.dial ? `+${country.dial} ` : "";
      const payload = {
        ...form,
        phone: form.phone ? `${dial}${form.phone}`.trim() : "",
        captcha_token,
      };
      await api.post("/demo-requests", payload);
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "demo_submit", {
          event_category: "conversion",
          event_label: form.property_type || "demo_form",
          rooms: form.rooms || "",
        });
      }
      setSent(true);
      toast.success(t("Kërkesa u dërgua. Do t'ju kontaktojmë së shpejti.", "Request received. We'll get back to you shortly."));
    } catch (err) {
      toast.error(formatAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  const propertyTypes = t(PROPERTY_TYPES_SQ, PROPERTY_TYPES_EN);
  const jobRoles = t(JOB_ROLES_SQ, JOB_ROLES_EN);

  return (
    <>
      <SEO
        title={t("Kërko Demo — Akomoda", "Request a Demo — Akomoda")}
        description={t(
          "Provoje Akomoda për hotelin tuaj. Demo falas — 30 minuta. Mbështetje në shqip dhe konfigurim brenda 48 orësh.",
          "See Akomoda in action for your hotel. Free 30-minute demo. Multilingual support and setup within 48 hours."
        )}
      />
      <PageHeader
        eyebrow={t("Demo Falas", "Free Demo")}
        title={t("Provojeni Akomoda për hotelin tuaj", "See Akomoda live for your hotel")}
        description={t(
          "Na thoni pak për pronën tuaj dhe ekipi do ju kontaktojë për të caktuar një demo të personalizuar.",
          "Tell us a little about your property and our team will reach out to schedule a personalised demo."
        )}
        breadcrumb={[{ label: t("Kërko Demo", "Request Demo") }]}
      />

      <section className="bg-surface-1">
        <div className="container-akm grid gap-10 py-14 md:grid-cols-12 md:py-20">
          <div className="md:col-span-7">
            {sent ? (
              <div className="card-akm" data-testid="demo-success">
                <CheckCircle2 size={28} className="text-[#FF7A00]" />
                <h2 className="h2-akm mt-3">{t("Faleminderit!", "Thank you!")}</h2>
                <p className="lead mt-2">
                  {t(
                    <>Kërkesa juaj shkoi. Ekipi i Akomoda do ju kontaktojë në <strong className="text-ink">{form.email}</strong> brenda 24 orëve.</>,
                    <>Your request is in. The Akomoda team will get back to you at <strong className="text-ink">{form.email}</strong> within 24 hours.</>
                  )}
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="demo-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t("Emri i plotë *", "Full name *")} value={form.name} onChange={set("name")} required testid="demo-name" />
                  <Field label={t("Email biznesi *", "Business email *")} type="email" value={form.email} onChange={set("email")} required testid="demo-email" />
                  <div>
                    <label className="block text-sm font-medium text-ink">{t("Nr. Cel", "Phone No.")}</label>
                    <div className="mt-1.5">
                      <PhoneInput
                        value={form.phone}
                        onChange={set("phone")}
                        country={country}
                        onCountryChange={setCountry}
                        placeholder={t("Nr. Cel", "Phone No.")}
                        testid="demo-phone"
                      />
                    </div>
                  </div>
                  <Field label={t("Emri i pronës / kompanisë", "Property / company name")} value={form.company} onChange={set("company")} testid="demo-company" />

                  <Select
                    label={t("Roli *", "Job role *")}
                    value={form.job_role}
                    onChange={set("job_role")}
                    options={jobRoles}
                    placeholder={t("Zgjidh rolin", "Select role")}
                    required
                    testid="demo-job-role"
                  />

                  <Select
                    label={t("Tipi i pronës", "Property type")}
                    value={form.property_type}
                    onChange={set("property_type")}
                    options={propertyTypes}
                    placeholder={t("Zgjidh tipin", "Select type")}
                    testid="demo-property-type"
                  />

                  <Select
                    label={t("Numri i dhomave", "Number of rooms")}
                    value={form.rooms}
                    onChange={set("rooms")}
                    options={ROOMS}
                    placeholder={t("Zgjidh", "Select")}
                    testid="demo-rooms"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-ink">{t("Çfarë doni të shihni në demo?", "What would you like to see in the demo?")}</label>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    rows={4}
                    className="mt-1.5 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/20"
                    placeholder={t(
                      "P.sh. si lidhet me Booking.com, check-in online, raportet financiare…",
                      "E.g. how it syncs with Booking.com, online check-in, financial reports…"
                    )}
                    data-testid="demo-message"
                  />
                </div>

                <RecaptchaDisclosure t={t} />

                <button type="submit" disabled={loading} className="btn-primary mt-5 disabled:opacity-60" data-testid="demo-submit">
                  {loading ? t("Duke dërguar…", "Sending…") : t("Dërgo kërkesën", "Send request")}
                </button>
                <p className="mt-3 text-xs text-slate2">
                  {t(
                    "Duke dërguar këtë formë, pranoni të kontaktoheni nga ekipi i Akomoda.",
                    "By submitting this form, you agree to be contacted by the Akomoda team."
                  )}
                </p>
              </form>
            )}
          </div>

          <aside className="md:col-span-5">
            <div className="card-akm">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate2">{t("Çfarë përfshin demo", "What the demo covers")}</div>
              <ul className="mt-3 space-y-2 text-sm text-ink">
                {[
                  t("30 minuta, e personalizuar për pronën tuaj",       "30 minutes, tailored to your property"),
                  t("Shohim PMS-in dhe Channel Manager-in",             "Walk through the PMS and Channel Manager"),
                  t("Pyetje teknike dhe çmime",                          "Technical questions and pricing"),
                  t("Plan konfigurimi për hotelin tuaj",                 "A setup plan for your hotel"),
                  t("Pa angazhime, pa kosto",                            "No commitment, no cost"),
                ].map((tt) => (
                  <li key={tt} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#FF7A00]" /> {tt}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 card-akm">
              <div className="font-display text-base font-bold text-ink">{t("Përgjigjemi brenda 24 orëve", "We reply within 24 hours")}</div>
              <p className="mt-1 text-sm text-slate2">
                {t(
                  <>Pas dërgimit, do ju vijë një konfirmim me email. Çdo komunikim shkon në <strong className="text-ink">info@akomoda.co</strong>.</>,
                  <>You&apos;ll receive an email confirmation right after. All correspondence goes to <strong className="text-ink">info@akomoda.co</strong>.</>
                )}
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink">{label}</label>
      <input
        {...rest}
        className="mt-1.5 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/20"
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder, required, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink">{label}</label>
      <select
        value={value}
        onChange={onChange}
        required={required}
        className="mt-1.5 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/20"
        data-testid={testid}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

export function RecaptchaDisclosure({ t }) {
  if (!RECAPTCHA_SITE_KEY) return null;
  return (
    <p className="mt-5 text-[11.5px] leading-relaxed text-slate2" data-testid="recaptcha-disclosure">
      {t(
        "Kjo faqe mbrohet nga reCAPTCHA dhe zbatohen ",
        "This site is protected by reCAPTCHA and the Google "
      )}
      <a
        href="https://policies.google.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-[#FF7A00]"
      >
        {t("Politika e Privatësisë", "Privacy Policy")}
      </a>
      {t(" dhe ", " and ")}
      <a
        href="https://policies.google.com/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-[#FF7A00]"
      >
        {t("Kushtet e Shërbimit", "Terms of Service")}
      </a>
      {t(" të Google.", " apply.")}
    </p>
  );
}
